import { useTranslation } from 'react-i18next';
import { CheckCircle2, CircleDashed, Clock, Receipt } from 'lucide-react';
import { Skeleton } from '@shared/ui';
import type { InvoiceStats } from '@entities/invoice';
import { formatAmount } from '@shared/lib/format-amount';

interface InvoiceStatsCardsProps {
  stats: InvoiceStats;
  isLoading?: boolean;
}

const STAT_ITEMS = [
  {
    key: 'total' as const,
    labelKey: 'payments.statistic.created',
    Icon: Receipt,
  },
  {
    key: 'paid' as const,
    labelKey: 'payments.statistic.payed',
    Icon: CheckCircle2,
  },
  {
    key: 'underpaid' as const,
    labelKey: 'payments.statistic.onPayment',
    Icon: CircleDashed,
  },
  {
    key: 'pending' as const,
    labelKey: 'payments.statistic.notPayed',
    Icon: Clock,
  },
];

const formatUsdStat = (value: number): string => `$${formatAmount(value, 2, true)}`;

export function InvoiceStatsCards({ stats, isLoading = false }: InvoiceStatsCardsProps) {
  const { t } = useTranslation('cabinet');

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-[4.5rem] w-full rounded-lg sm:h-[5rem] sm:rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
      {STAT_ITEMS.map(({ key, labelKey, Icon }) => (
        <div key={key} className="stat-card flex flex-col justify-between gap-1.5 sm:gap-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="stat-card-label">{t(labelKey)}</h3>
            <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          </div>
          <p className="stat-card-value">{formatUsdStat(stats[key])}</p>
        </div>
      ))}
    </div>
  );
}
